import React, { useMemo, useRef, useState, useEffect } from "react";
import "../styles/GiftPage.css";
import Header from "./Header";
import Footer from "./Footer";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/Language";
import {
  getMembershipDuration,
  getMembershipPrice,
  getMembershipTier,
  membershipDurations,
  membershipTiers,
} from "../utils/constants";

const CHECKOUT_ENABLED = false;
const MANUAL_PAYMENT_ENABLED = true;

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER;
const BACKEND = process.env.REACT_APP_BACKEND;

const GIFT_CARDS = [
  {
    id: "not-yet",
    mood: "funny",
    eyebrow: {
      en: "A gentle reminder",
      fr: "Un petit rappel",
      es: "Un pequeño recordatorio",
      ar: "تذكير بسيط",
      it: "Un piccolo promemoria",
    },
    label: {
      en: "You're not getting married yet.",
      fr: "Tu ne te maries toujours pas.",
      es: "Todavía no te casas.",
      ar: "ما زلت لم تتزوج.",
      it: "Non ti sposi ancora.",
    },
    sublabel: {
      en: "So we thought we would help.",
      fr: "Alors on s'est dit qu'on allait t'aider.",
      es: "Así que pensamos en ayudarte.",
      ar: "فقلنا نساعدك قليلاً.",
      it: "Quindi abbiamo pensato di aiutarti.",
    },
    theme: "coral",
    image: null,
  },
  {
    id: "no-pressure",
    mood: "funny",
    eyebrow: {
      en: "No pressure",
      fr: "Sans pression",
      es: "Sin presión",
      ar: "بدون ضغط",
      it: "Senza pressioni",
    },
    label: {
      en: "No pressure. But we found you a head start.",
      fr: "Aucune pression. Mais on t'a trouvé un coup de pouce.",
      es: "Sin presión. Pero te encontramos un empujón.",
      ar: "لا ضغط. لكن وجدنا لك بداية.",
      it: "Nessuna pressione. Ma ti abbiamo trovato una spinta.",
    },
    sublabel: {
      en: "With love, obviously.",
      fr: "Avec amour, évidemment.",
      es: "Con cariño, obviamente.",
      ar: "بكل حب طبعاً.",
      it: "Con affetto, naturalmente.",
    },
    theme: "yellow",
    image: null,
  },
  {
    id: "something-real",
    mood: "thoughtful",
    eyebrow: {
      en: "For someone special",
      fr: "Pour quelqu'un de spécial",
      es: "Para alguien especial",
      ar: "لشخص مميز",
      it: "Per una persona speciale",
    },
    label: {
      en: "For the one who deserves something real.",
      fr: "Pour la personne qui mérite quelque chose de vrai.",
      es: "Para quien merece algo verdadero.",
      ar: "لمن يستحق شيئاً حقيقياً.",
      it: "Per chi merita qualcosa di vero.",
    },
    sublabel: {
      en: "A thoughtful beginning, chosen for you.",
      fr: "Un nouveau départ choisi pour toi.",
      es: "Un nuevo comienzo elegido para ti.",
      ar: "بداية جميلة اخترناها لك.",
      it: "Un nuovo inizio scelto per te.",
    },
    theme: "rose",
    image: null,
  },
  {
    id: "family",
    mood: "warm",
    eyebrow: {
      en: "From your family",
      fr: "De la part de ta famille",
      es: "De parte de tu familia",
      ar: "من عائلتك",
      it: "Dalla tua famiglia",
    },
    label: {
      en: "Because we want to see you happy.",
      fr: "Parce qu'on veut te voir heureux.",
      es: "Porque queremos verte feliz.",
      ar: "لأننا نريد أن نراك سعيداً.",
      it: "Perché vogliamo vederti felice.",
    },
    sublabel: {
      en: "The rest is entirely up to you.",
      fr: "La suite t'appartient entièrement.",
      es: "El resto depende completamente de ti.",
      ar: "والباقي متروك لك.",
      it: "Il resto dipende completamente da te.",
    },
    theme: "green",
    image: null,
  },
  {
    id: "mabrook-advance",
    mood: "celebration",
    eyebrow: {
      en: "We are planning ahead",
      fr: "On prend un peu d'avance",
      es: "Nos adelantamos un poco",
      ar: "نبارك لك مسبقاً",
      it: "Ci portiamo avanti",
    },
    label: {
      en: "Mabrook, in advance.",
      fr: "Mabrook, un peu en avance.",
      es: "Mabrook, por adelantado.",
      ar: "مبروك مسبقاً.",
      it: "Mabrook, in anticipo.",
    },
    sublabel: {
      en: "Consider this the first step.",
      fr: "Considère ceci comme la première étape.",
      es: "Considera este el primer paso.",
      ar: "اعتبرها الخطوة الأولى.",
      it: "Consideralo il primo passo.",
    },
    theme: "blue",
    image: null,
  },
  {
    id: "possibilities",
    mood: "minimal",
    eyebrow: {
      en: "A new chapter",
      fr: "Un nouveau chapitre",
      es: "Un nuevo capítulo",
      ar: "فصل جديد",
      it: "Un nuovo capitolo",
    },
    label: {
      en: "No pressure. Just possibilities.",
      fr: "Aucune pression. Juste des possibilités.",
      es: "Sin presión. Solo posibilidades.",
      ar: "بدون ضغط. فقط احتمالات جميلة.",
      it: "Nessuna pressione. Solo possibilità.",
    },
    sublabel: {
      en: "Take your time. Make it meaningful.",
      fr: "Prends ton temps. Fais-en quelque chose de vrai.",
      es: "Tómate tu tiempo. Haz que valga la pena.",
      ar: "خذ وقتك واجعلها تجربة ذات معنى.",
      it: "Prenditi il tuo tempo. Rendilo significativo.",
    },
    theme: "cream",
    image: null,
  },
];

const MAX_MESSAGE = 200;

const localize = (value, lang) => value?.[lang] || value?.en || "";
const formatPrice = (value) => `${Math.round(Number(value))} MAD`;

const GiftPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const plansRef = useRef(null);

  const lang = language?.value || "en";
  const isRTL = lang === "ar";

  const [selectedCard, setSelectedCard] = useState(GIFT_CARDS[0].id);
  const [selectedTier, setSelectedTier] = useState("premium");
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [senderName, setSenderName] = useState("");
  const [message, setMessage] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [buyerEmail, setBuyerEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [reference, setReference] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    console.log("[gift] clearing reference");
    setReference(null);
    setRequestError("");
  }, [selectedTier, selectedPlan, selectedCard, senderName, message]);

  const toggleFaq = (faqId) => {
    setOpenFaq((current) => (current === faqId ? null : faqId));
  };

  const card = useMemo(
    () => GIFT_CARDS.find((item) => item.id === selectedCard) || GIFT_CARDS[0],
    [selectedCard],
  );

  const tier = useMemo(() => getMembershipTier(selectedTier), [selectedTier]);

  const plan = useMemo(
    () => getMembershipDuration(selectedPlan),
    [selectedPlan],
  );

  const planPrice = getMembershipPrice(tier.id, plan.id);
  const monthlyEquivalent = planPrice / plan.months;

  const cardLabel = card.label[lang] || card.label.en;

  const cardEyebrow = card.eyebrow[lang] || card.eyebrow.en;
  const cardSublabel = card.sublabel[lang] || card.sublabel.en;

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleContinue = async () => {
    if (!MANUAL_PAYMENT_ENABLED || requesting) return;

    const cleanEmail = buyerEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
      setRequestError(t("Please enter a valid email so we can reach you."));
      return;
    }

    setRequestError("");
    setRequesting(true);

    try {
      const response = await fetch(`${BACKEND}/website/gift-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          buyerEmail: cleanEmail,
          buyerLanguage: lang,
          tier: tier.id,
          durationMonths: plan.months,
          senderName: senderName.trim(),
          message: message.trim(),
          cardId: selectedCard,
        }),
      });
      const data = await response.json();

      if (data.status === "201") {
        setReference({ code: data.reference, amount: data.amountMAD });
      } else {
        setRequestError(
          data.message?.[lang] ||
            data.message?.en ||
            t("Something went wrong."),
        );
      }
    } catch {
      setRequestError(t("Something went wrong. Please try again."));
    } finally {
      setRequesting(false);
    }
  };

  const whatsappLink = () => {
    const text = [
      t("Hello, I would like to gift a Mabrook membership."),
      "",
      `${t("Reference")}: ${reference.code}`,
      `${t("Plan")}: ${t(tier.labelKey)} ${localize(plan.label, lang)}`,
      `${t("Amount")}: ${reference.amount} MAD`,
      `${t("Your email")}: ${buyerEmail.trim().toLowerCase()}`,
    ].join("\n");
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  return (
    <>
      <Header />

      <main
        className={`gift-page gift-page-tier-${tier.id}`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <section className="gift-hero">
          <div className="gift-container">
            <div className="gift-hero-layout">
              <div className="gift-hero-copy">
                <span className="gift-hero-eyebrow">
                  {t("A meaningful gift from Mabrook")}
                </span>

                <h1>{t("Give them a little help finding something real.")}</h1>

                <p>
                  {t(
                    "Offer a Mabrook membership to a friend, sibling or someone you love. Thoughtful, private and entirely on their terms.",
                  )}
                </p>

                <button
                  type="button"
                  className="gift-primary-cta"
                  onClick={scrollToPlans}
                >
                  {t("Gift a Mabrook membership")}
                  <span aria-hidden="true">→</span>
                </button>
              </div>

              <div className="gift-hero-art" aria-hidden="true">
                <div className="gift-hero-card gift-hero-card-back">
                  <span className="gift-card-brand">Mabrook</span>
                  <strong>{t("No pressure.")}</strong>
                  <small>{t("Just possibilities.")}</small>
                </div>

                <div className="gift-hero-card gift-hero-card-middle">
                  <span className="gift-card-brand">Mabrook</span>
                  <strong>{t("Mabrook, in advance.")}</strong>
                </div>

                <div className="gift-hero-card gift-hero-card-front">
                  <span className="gift-card-brand">Mabrook</span>
                  <span className="gift-hero-card-kicker">
                    {t("A gentle reminder")}
                  </span>
                  <strong>{t("You're not getting married yet.")}</strong>
                  <small>{t("So we thought we would help.")}</small>
                </div>
              </div>
            </div>

            <div className="gift-steps">
              <article className="gift-step">
                <span>1</span>
                <div>
                  <h3>{t("Personalize it")}</h3>
                  <p>{t("Choose a card and add your own private message.")}</p>
                </div>
              </article>

              <article className="gift-step">
                <span>2</span>
                <div>
                  <h3>{t("Send it")}</h3>
                  <p>{t("We send you the code. You pass it on.")}</p>
                </div>
              </article>

              <article className="gift-step">
                <span>3</span>
                <div>
                  <h3>{t("They redeem it")}</h3>
                  <p>
                    {t(
                      "The recipient redeems the gift code in the app. If they already have an active membership, the gifted membership is safely added to their account and starts automatically when the membership ahead of it ends.",
                    )}
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="gift-gallery-section">
          <div className="gift-container">
            <header className="gift-section-heading gift-section-heading-numbered">
              <span className="gift-section-index">01</span>
              <div>
                <h2>{t("Choose your card")}</h2>
                <p>
                  {t(
                    "Funny, heartfelt or understated. Choose the message that feels most like you.",
                  )}
                </p>
              </div>
            </header>
          </div>

          <div className="gift-gallery-scroll">
            {GIFT_CARDS.map((item) => {
              const isSelected = selectedCard === item.id;
              const label = item.label[lang] || item.label.en;
              const eyebrow = item.eyebrow[lang] || item.eyebrow.en;
              const sublabel = item.sublabel[lang] || item.sublabel.en;

              return (
                <button
                  key={item.id}
                  type="button"
                  className={`gift-gallery-card gift-theme-${item.theme} ${
                    item.image ? "has-image" : ""
                  } ${isSelected ? "selected" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedCard(item.id)}
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt=""
                      className="gift-gallery-card-image"
                    />
                  )}

                  <span className="gift-gallery-overlay" />

                  <span className="gift-gallery-top">
                    <span className="gift-gallery-logo">Mabrook</span>

                    <span className="gift-gallery-check" aria-hidden="true">
                      ✓
                    </span>
                  </span>

                  <span className="gift-gallery-copy">
                    <small>{eyebrow}</small>
                    <strong>{label}</strong>
                    <span>{sublabel}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="gift-personalization-section">
          <div className="gift-container">
            <header className="gift-section-heading gift-section-heading-numbered">
              <span className="gift-section-index">02</span>
              <div>
                <h2>{t("Add your name and message")}</h2>
                <p>
                  {t(
                    "Add a simple message. The recipient's experience remains completely private.",
                  )}
                </p>
              </div>
            </header>

            <div className="gift-personalization-panel">
              <div className="gift-personalization-form">
                <div className="gift-form-heading">
                  <span>{t("Gift details")}</span>
                  <small>{t("All fields are optional")}</small>
                </div>

                <label className="gift-field">
                  <span className="gift-field-label">{t("Your name")}</span>

                  <input
                    type="text"
                    value={senderName}
                    maxLength={40}
                    autoComplete="name"
                    onChange={(event) => setSenderName(event.target.value)}
                    placeholder={t("How should your name appear?")}
                  />
                </label>

                <label className="gift-field">
                  <span className="gift-field-label">{t("Your message")}</span>

                  <textarea
                    value={message}
                    maxLength={MAX_MESSAGE}
                    rows={5}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder={t(
                      "Write a few words for the person receiving the gift.",
                    )}
                  />

                  <span className="gift-character-count">
                    {message.length} / {MAX_MESSAGE}
                  </span>
                </label>

                {/*
                //Removed this, we don't want the person to be notified
                <div className="gift-privacy-note">
                  <span className="gift-privacy-icon" aria-hidden="true">
                    i
                  </span>

                  <p>
                    {t(
                      "You will only be notified when the gift is accepted. The recipient's profile and activity remain private.",
                    )}
                  </p>
                </div>*/}
              </div>

              <div className="gift-preview-column">
                <div className="gift-preview-heading">
                  <span>{t("Preview")}</span>
                  <small>{t("What the recipient will see")}</small>
                </div>

                <div
                  className={`gift-preview-card gift-theme-${card.theme} ${
                    card.image ? "has-image" : ""
                  }`}
                  style={
                    card.image
                      ? {
                          backgroundImage: `linear-gradient(
            to top,
            rgba(16, 24, 40, 0.72),
            rgba(16, 24, 40, 0.08)
          ), url(${card.image})`,
                        }
                      : undefined
                  }
                >
                  <div className="gift-preview-top">
                    <span>MABROOK {t(tier.labelKey)}</span>
                    <span>{localize(plan.label, lang)}</span>
                  </div>

                  <div className="gift-preview-content">
                    <span className="gift-preview-kicker">{cardEyebrow}</span>

                    <h3>{cardLabel}</h3>

                    {!message && (
                      <p className="gift-preview-placeholder">{cardSublabel}</p>
                    )}

                    {message && (
                      <p className="gift-preview-message">{message}</p>
                    )}

                    {senderName && (
                      <p className="gift-preview-sender">
                        {t("From")} {senderName}
                      </p>
                    )}
                  </div>

                  {/*<div className="gift-preview-bottom">
                    <span>
                      {plan.months}{" "}
                      {plan.months === 1 ? t("month") : t("months")}
                    </span>
                    <span>{formatPrice(planPrice)}</span>
                  </div>*/}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="gift-plan-section" ref={plansRef}>
          <div className="gift-container">
            <header className="gift-section-heading gift-section-heading-numbered gift-section-heading-light">
              <span className="gift-section-index">03</span>
              <div>
                <h2>{t("Choose the membership and duration")}</h2>
                <p>
                  {t(
                    "Every gift is a one-time purchase and never renews automatically.",
                  )}
                </p>
              </div>
            </header>

            <div className="gift-tier-choice">
              <div className="gift-tier-choice-heading">
                <span>{t("Choose the membership level")}</span>
                <small>{t("Prices shown in MAD")}</small>
              </div>

              <div
                className="gift-tier-switcher"
                role="radiogroup"
                aria-label={t("Membership level")}
              >
                {membershipTiers.map((item) => {
                  const isSelected = item.id === tier.id;
                  const startingPrice = getMembershipPrice(item.id, "monthly");

                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`${isSelected ? "selected" : ""} tier-${item.id}`}
                      onClick={() => setSelectedTier(item.id)}
                    >
                      <span className="gift-tier-radio" aria-hidden="true">
                        <span />
                      </span>
                      <span className="gift-tier-copy">
                        <strong>Mabrook {t(item.labelKey)}</strong>
                        <small>{t(item.descriptionKey)}</small>
                      </span>
                      <span className="gift-tier-price">
                        <small>{t("From")}</small>
                        <strong>{formatPrice(startingPrice)}</strong>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`gift-plans-grid gift-tier-${tier.id}`}>
              {membershipDurations.map((item) => {
                const isSelected = selectedPlan === item.id;
                const price = getMembershipPrice(tier.id, item.id);
                const equivalent = price / item.months;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`gift-plan ${isSelected ? "selected" : ""}`}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedPlan(item.id)}
                  >
                    {item.id === "quarterly" && (
                      <span className="gift-plan-badge">
                        {t("Most selected")}
                      </span>
                    )}

                    <span className="gift-plan-radio" aria-hidden="true">
                      <span />
                    </span>

                    <span className="gift-plan-duration">
                      {localize(item.label, lang)}
                    </span>

                    <span className="gift-plan-description">
                      {t(item.descriptionKey)}
                    </span>

                    <span className="gift-plan-price">
                      <strong>{formatPrice(price)}</strong>
                    </span>

                    <span className="gift-plan-equivalent">
                      {formatPrice(equivalent)} / {t("month")}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="gift-checkout">
              <div className="gift-order-summary">
                <span>{t("Your selection")}</span>

                <strong>
                  Mabrook {t(tier.labelKey)} · {localize(plan.label, lang)} ·{" "}
                  {formatPrice(planPrice)}
                </strong>
              </div>

              {reference ? (
                <div className="gift-reference-box">
                  <p className="gift-reference-label">{t("Your reference")}</p>
                  <div className="gift-reference-row">
                    <div className="gift-reference-field">
                      <span className="gift-reference-code">
                        {reference.code}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="gift-copy-button"
                      onClick={() => {
                        navigator.clipboard?.writeText(reference.code);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? t("Copied") : t("Copy")}
                    </button>
                  </div>
                  <p className="gift-reference-note">
                    {t(
                      "Send us this reference on WhatsApp. We will reply with the bank details, and send you the gift code once the transfer is confirmed.",
                    )}
                  </p>
                  <a
                    className="gift-whatsapp-button"
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Continue on WhatsApp")}
                  </a>
                </div>
              ) : (
                <>
                  <label className="gift-field">
                    <span className="gift-field-label">{t("Your email")}</span>
                    <input
                      type="email"
                      value={buyerEmail}
                      onChange={(e) => {
                        setBuyerEmail(e.target.value);
                        if (requestError) setRequestError("");
                      }}
                      placeholder={t("youremail@example.com")}
                      autoComplete="email"
                    />
                  </label>

                  <p className="gift-email-hint">
                    {t(
                      "We send the gift code to you, not to the recipient. You choose how and when to pass it on.",
                    )}{" "}
                    {t(
                      "The gift is added to the recipient's Mabrook account when they redeem the code.",
                    )}
                  </p>

                  {requestError && <p className="gift-error">{requestError}</p>}

                  <button
                    type="button"
                    className="gift-checkout-button"
                    onClick={handleContinue}
                    disabled={requesting}
                  >
                    <span>
                      {requesting ? t("Please wait...") : t("Continue")}
                    </span>
                    <span aria-hidden="true">→</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="gift-faq-section">
          <div className="gift-container gift-faq-layout">
            <div className="gift-faq-introduction">
              <span className="gift-section-index">04</span>
              <h2>{t("Questions about gifting")}</h2>
              <p>
                {t(
                  "Everything the sender and recipient should know before getting started.",
                )}
              </p>
            </div>

            <div className="gift-faq">
              <details open={openFaq === "account"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("account");
                  }}
                >
                  <span>{t("Does the recipient need a Mabrook account?")}</span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "No. If they do not have an account, they can create one when they receive and accept the gift.",
                  )}
                </p>
              </details>

              <details open={openFaq === "start"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("start");
                  }}
                >
                  <span>{t("When does the membership start?")}</span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "The membership starts the moment the recipient redeems the gift code in the app.",
                  )}
                </p>
              </details>

              <details open={openFaq === "privacy"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("privacy");
                  }}
                >
                  <span>{t("Can I see their activity on Mabrook?")}</span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "No. Their profile, conversations and activity remain completely private.",
                  )}
                </p>
              </details>

              <details open={openFaq === "existing-membership"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("existing-membership");
                  }}
                >
                  <span>
                    {t("What if the recipient already has a membership?")}
                  </span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "They can still redeem the gift. Their current membership is not lost or shortened. The gifted membership is added after any membership time they already have.",
                  )}
                </p>
              </details>

              <details open={openFaq === "redeemed-status"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("redeemed-status");
                  }}
                >
                  <span>
                    {t("Can I know when they redeemed the gift code?")}
                  </span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "No. We do not notify you when the gift is redeemed. The recipient's use of Mabrook remains private.",
                  )}
                </p>
              </details>

              <details open={openFaq === "recipient-code"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("recipient-code");
                  }}
                >
                  <span>{t("Who receives the gift code?")}</span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "You do. We send the gift code to the email address you provide, and you choose how and when to give it to the recipient.",
                  )}
                </p>
              </details>

              <details open={openFaq === "validity"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("validity");
                  }}
                >
                  <span>{t("How long is the gift code valid?")}</span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "The gift code is valid for six months from the date your payment is confirmed.",
                  )}
                </p>
              </details>

              <details open={openFaq === "plan-change"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("plan-change");
                  }}
                >
                  <span>
                    {t("Can the recipient change the membership I selected?")}
                  </span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "No. The gift code is created for the membership level and duration you selected when purchasing the gift.",
                  )}
                </p>
              </details>

              <details open={openFaq === "renewal"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("renewal");
                  }}
                >
                  <span>{t("Does the gift renew automatically?")}</span>
                  <span className="gift-faq-symbol" aria-hidden="true" />
                </summary>

                <p>
                  {t(
                    "No. The gift is a single payment for a fixed membership period.",
                  )}
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default GiftPage;
